import { MRT_ColumnDef, MRT_Row, MRT_RowData, MRT_TableInstance } from 'material-react-table';
import { Dispatch, FC, SetStateAction, useMemo, useState } from 'react';
import { Defect } from '../types';
import { useTranslation } from 'react-i18next';
import OverflowTooltip from '@shared/components/ui/OverflowTooltip';
import { useSelector } from 'react-redux';
import { selectDefects, selectDefectsCount, selectGetDefectsStatus } from '../store/selectors';
import CustomTable, { getCustomMuiFilterTextFieldProps } from '@shared/components/others/CustomTable/CustomTable';
import UtmsChip from '@shared/components/ui/UtmsChip';
import { ColumnFilters, Pagination, RequestStatusEnum, Sorting, UnknownObject } from '@shared/types';
import DefectsTableToolbar from './DefectsTableToolbar';
import { convertToTranslationKey, toTitleCase } from '@shared/utils/utilsOld';
import UtmsPagination from '@shared/components/ui/UtmsPagination';
import { Box, Link, MenuItem, Typography } from '@mui/material';
import { DEFECT_STATUS_COLOR, DEFECT_TYPE_COLOR, defectStatuses, defectTypes } from '../constants';
import useMultiRowSelection from '@shared/hooks/useMultiRowSelection';
import EmptyList from '@shared/components/others/EmptyList/EmptyList';
import { useNavigate, useParams } from 'react-router-dom';
import { UI_DEFECTS_PATH } from '@shared/constants/pathsConstants';
import DefectFormDialog from './DefectFormDialog';

interface DefectsTableProps {
    showColumnFilters: boolean;
    setShowColumnFilters: Dispatch<SetStateAction<boolean>>;
    rowSelection: UnknownObject;
    setRowSelection: Dispatch<SetStateAction<UnknownObject>>;
    pagination: Pagination;
    setPagination: Dispatch<SetStateAction<Pagination>>;
    columnFilters: ColumnFilters;
    setColumnFilters: Dispatch<SetStateAction<ColumnFilters>>;
    sorting: Sorting;
    setSorting: Dispatch<SetStateAction<Sorting>>;
    testcaseId?: number;
}

const DefectsTable: FC<DefectsTableProps> = ({
    rowSelection,
    pagination,
    setPagination,
    setRowSelection,
    columnFilters,
    sorting,
    setSorting,
    setColumnFilters,
    showColumnFilters,
    setShowColumnFilters,
    testcaseId,
}) => {
    const { t } = useTranslation();
    const defects = useSelector(selectDefects);
    const getDefectsStatus = useSelector(selectGetDefectsStatus);
    const isDefectsLoading = getDefectsStatus === RequestStatusEnum.Loading;
    const defectsCount = useSelector(selectDefectsCount);
    const { handlePaginationChange, tableProps } = useMultiRowSelection<Defect>({
        rowSelection,
        setRowSelection,
        setPagination,
        enableRowClickSelection: false,
    });
    const [openDefectAddDialog, setOpenDefectAddDialog] = useState(false);
    const navigate = useNavigate();
    const { defectId } = useParams();
    const isFilteringApplied = useMemo(() => {
        return Boolean(columnFilters.length);
    }, [columnFilters]);
    const linkMode = Boolean(testcaseId);

    const handleClearAllFilters = () => {
        setColumnFilters([]);
    };

    const columns = useMemo<MRT_ColumnDef<Defect>[]>(
        () => [
            {
                accessorKey: 'pk',
                header: 'ID',
                enableColumnActions: false,
            },
            {
                accessorKey: 'name',
                header: t('name'),
                Cell: ({ cell }) => {
                    const name = String(cell.getValue());
                    return (
                        <OverflowTooltip title={name} maxLines={6}>
                            {name}
                        </OverflowTooltip>
                    );
                },
                enableSorting: false,
            },
            {
                accessorKey: 'jiraIssueKey',
                header: t('jira_issue'),
                Cell: ({ cell }) => {
                    const defect = cell.row.original;
                    const { jiraIssueKey, jiraIssueUrl } = defect;

                    return jiraIssueKey ? (
                        <Link underline="hover" href={jiraIssueUrl} target="_blank">
                            {jiraIssueKey}
                        </Link>
                    ) : (
                        '–'
                    );
                },
                enableSorting: false,
            },
            {
                accessorKey: 'status',
                header: t('status'),
                enableSorting: false,
                Cell: ({ cell }) => {
                    const status = cell.getValue() as Defect['status'];
                    if (!status) {
                        return '–';
                    }
                    return <UtmsChip label={t(status.toLowerCase())} color={DEFECT_STATUS_COLOR[status]} />;
                },
                filterVariant: 'select',
                filterSelectOptions: defectStatuses.map((item) => ({
                    label: toTitleCase(t(convertToTranslationKey(item))),
                    value: item,
                })),
                muiFilterTextFieldProps: ({ column }) => ({
                    ...getCustomMuiFilterTextFieldProps(column, t),
                    SelectProps: {
                        renderValue: (selected) => {
                            if (!selected) return '–';
                            const status = selected as Defect['status'];
                            return (
                                <UtmsChip
                                    label={toTitleCase(t(convertToTranslationKey(status)))}
                                    color={DEFECT_STATUS_COLOR[status]}
                                />
                            );
                        },
                    },
                    children: defectStatuses.map((status) => (
                        <MenuItem key={status} value={status}>
                            <UtmsChip
                                label={toTitleCase(t(convertToTranslationKey(status)))}
                                color={DEFECT_STATUS_COLOR[status]}
                            />
                        </MenuItem>
                    )),
                }),
            },
            {
                accessorKey: 'type',
                header: t('type'),
                enableSorting: false,
                Cell: ({ cell }) => {
                    const type = cell.getValue() as Defect['type'];
                    if (!type) {
                        return '–';
                    }
                    return <UtmsChip label={t(type.toLowerCase())} color={DEFECT_TYPE_COLOR[type]} />;
                },
                filterVariant: 'select',
                filterSelectOptions: defectTypes.map((item) => ({
                    label: toTitleCase(t(convertToTranslationKey(item))),
                    value: item,
                })),
                muiFilterTextFieldProps: ({ column }) => ({
                    ...getCustomMuiFilterTextFieldProps(column, t),
                    SelectProps: {
                        renderValue: (selected) => {
                            if (!selected) return '–';
                            const type = selected as Defect['type'];
                            return (
                                <UtmsChip
                                    label={toTitleCase(t(convertToTranslationKey(type)))}
                                    color={DEFECT_TYPE_COLOR[type]}
                                />
                            );
                        },
                    },
                    children: defectTypes.map((type) => (
                        <MenuItem key={type} value={type}>
                            <UtmsChip
                                label={toTitleCase(t(convertToTranslationKey(type)))}
                                color={DEFECT_TYPE_COLOR[type]}
                            />
                        </MenuItem>
                    )),
                }),
            },
            {
                accessorKey: 'testRunsCount',
                header: t('test_runs'),
                enableColumnFilter: false,
                enableColumnActions: false,
            },

            {
                accessorKey: 'testResultsCount',
                header: t('test_results'),
                enableColumnFilter: false,
                enableColumnActions: false,
            },
            {
                accessorKey: 'testCasesCount',
                header: t('test_cases'),
                enableColumnFilter: false,
                enableColumnActions: false,
            },
        ],
        [t],
    );

    const handleOpenDefectAddDialog = () => {
        setOpenDefectAddDialog(true);
    };

    const handleCloseDefectAddDialog = () => {
        setOpenDefectAddDialog(false);
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                height: `calc(100vh - ${linkMode ? 433 : 220}px)`,
                overflow: 'hidden',
            }}
        >
            {/*@ts-ignore */}
            <CustomTable
                tableId="defects-table"
                columns={columns}
                data={defects}
                variant="elevation"
                muiTablePaperProps={{
                    sx: {
                        minWidth: 0,
                    },
                }}
                toolbar={(table: MRT_TableInstance<MRT_RowData>) => (
                    <DefectsTableToolbar
                        table={table}
                        columnFilters={columnFilters}
                        rowSelection={rowSelection}
                        handleOpenDefectAddDialog={handleOpenDefectAddDialog}
                        testcaseId={testcaseId}
                    />
                )}
                state={{
                    showSkeletons: isDefectsLoading,
                    showColumnFilters,
                    rowSelection,
                    pagination,
                    sorting,
                    columnFilters,
                }}
                getRowId={(row: Defect) => row.pk}
                onColumnFiltersChange={setColumnFilters}
                onShowColumnFiltersChange={setShowColumnFilters}
                onSortingChange={setSorting}
                {...tableProps}
                muiTableBodyRowProps={({ row }: { row: MRT_Row<Defect> }) => {
                    const isActive = defectId === String(row.original.pk);
                    const bg = isActive ? 'primary.selected' : 'inherit';
                    return {
                        onClick:
                            isActive || linkMode ? undefined : () => navigate(`/${UI_DEFECTS_PATH}/${row.original.pk}`),
                        sx: {
                            backgroundColor: bg,
                            // полностью убираем дефолтный фон MUI для selected (+ на hover)
                            '&&.Mui-selected, &&.Mui-selected:hover': {
                                backgroundColor: bg,
                                backgroundImage: 'none',
                            },
                            '& td:after': {
                                backgroundColor: 'transparent',
                            },
                            cursor: linkMode ? 'auto' : 'pointer',
                        },
                    };
                }}
                renderBottomToolbar={() => (
                    <UtmsPagination
                        view="table"
                        pagination={pagination}
                        count={defectsCount}
                        handleChangePagination={handlePaginationChange}
                        sx={{
                            '& .MuiSelect-root': {
                                marginRight: 0,
                            },
                        }}
                    />
                )}
                renderEmptyRowsFallback={() => {
                    return columnFilters.length === 0 ? (
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: 1,
                                p: 2,
                                color: 'text.disabled',
                            }}
                        >
                            <Typography>{t('no_defects_yet')}</Typography>
                        </Box>
                    ) : (
                        <EmptyList
                            getListStatus={getDefectsStatus}
                            count={defectsCount}
                            isFilteringApplied={isFilteringApplied}
                            handleClearAllFilters={handleClearAllFilters}
                            objectsForTranslationLabel={t('defects_r')}
                            favoritesMode={false}
                        />
                    );
                }}
                muiTableContainerProps={{
                    sx: {
                        maxWidth: '100%',
                        flex: 1,
                        overflow: 'auto',
                    },
                }}
            />
            <DefectFormDialog open={openDefectAddDialog} onClose={handleCloseDefectAddDialog} mode="create" />
        </Box>
    );
};

export default DefectsTable;
